import { mapCompetitorSocialAccounts } from "./competitorSocialMappers"
import { mapEntityWorldGeography } from "./entitySearchMappers"

function trimStr(value) {
  return String(value ?? "").trim()
}

function positiveInt(value) {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

/** New API nests fields under `entity`; legacy rows are flat on the compset row. */
function resolveCompsetSource(row) {
  if (!row || typeof row !== "object") return {}
  const entity = row.entity
  if (entity && typeof entity === "object") {
    return {
      ...entity,
      propertyType: row.propertyType ?? entity.propertyType,
      location: row.location ?? entity.location,
      entityId: entity.id ?? row.entityId,
    }
  }
  return row
}

/** Parse compset `location` when no structured geography is returned. */
function parseCompsetLocationString(location) {
  const loc = trimStr(location)
  if (!loc) {
    return { city: "", country: "", location: "" }
  }

  const parts = loc.split(",").map((part) => part.trim()).filter(Boolean)
  if (parts.length >= 2) {
    return {
      city: parts.slice(0, -1).join(", "),
      country: parts[parts.length - 1],
      location: loc,
    }
  }

  return { city: loc, country: "", location: loc }
}

function mapCompsetGeography(source) {
  const worldCity = source?.worldCity ?? source?.geographyCity ?? null
  if (worldCity && typeof worldCity === "object") {
    return mapEntityWorldGeography(worldCity)
  }

  return parseCompsetLocationString(source?.location)
}

function mapCompsetBrandFields(source) {
  const subBrandObj = source?.subBrand ?? source?.sub_brand ?? null
  const brandObj = source?.brand ?? null

  if (subBrandObj && typeof subBrandObj === "object") {
    const nestedBrand = subBrandObj.brand ?? subBrandObj.Brand ?? null
    const subBrandName = trimStr(
      subBrandObj.subBrand ?? subBrandObj.sub_brand ?? subBrandObj.name,
    )
    const brandName = trimStr(
      nestedBrand?.brand ?? nestedBrand?.name ?? brandObj?.brand ?? brandObj?.name,
    )

    return {
      brand: brandName && brandName !== "Unknown" ? brandName : "",
      brandId: positiveInt(nestedBrand?.id ?? brandObj?.id),
      subBrand: subBrandName && subBrandName !== "Unknown" ? subBrandName : "",
      subBrandId: positiveInt(subBrandObj.id ?? subBrandObj.subBrandId ?? subBrandObj.sub_brand_id),
      type: trimStr(source?.propertyType),
    }
  }

  const propertyType = trimStr(source?.propertyType)
  const directBrand = trimStr(brandObj?.brand ?? brandObj?.name ?? source?.brandName)

  return {
    brand: directBrand && directBrand !== "Unknown" ? directBrand : "",
    brandId: positiveInt(brandObj?.id),
    subBrand: propertyType && propertyType !== "Unknown" ? propertyType : "",
    subBrandId: null,
    type: propertyType,
  }
}

export function mapCompsetCompetitor(row) {
  if (!row || row.id == null) return null

  const source = resolveCompsetSource(row)
  const socialChannels = mapCompetitorSocialAccounts(source.socialNetworkAccounts)
  const geography = mapCompsetGeography(source)
  const brandFields = mapCompsetBrandFields(source)
  const websiteUrl = trimStr(source.websiteUrl ?? source.website)
  const tripadvisorUrl = trimStr(source.url ?? source.tripadvisorUrl ?? source.tripAdvisorUrl)
  const entityId = positiveInt(source.entityId ?? source.id)

  return {
    id: String(row.id),
    compsetId: row.id,
    entityId,
    property: trimStr(source.name),
    type: brandFields.type,
    location: geography.location,
    country: geography.country,
    countryId: geography.countryId ?? null,
    city: geography.city,
    cityId: geography.cityId ?? null,
    brand: brandFields.brand,
    brandId: brandFields.brandId,
    subBrand: brandFields.subBrand,
    subBrandId: brandFields.subBrandId,
    websiteUrl,
    tripadvisorUrl,
    order: row.order ?? 0,
    socialChannels,
  }
}

export function mapCompsetCompetitors(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(mapCompsetCompetitor)
    .filter(Boolean)
    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
}

export function unwrapClientCompsetResponse(payload) {
  if (!payload || typeof payload !== "object") return null
  return payload
}
